import { ButtonGroup, Card, Separator, toast, useAlertDialog } from "@overmap-ai/blocks"
import {
	type Asset,
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
	type Procedure,
	type ProcedureFieldValuesAttachment,
	type ProcedureInitiative,
	type Stored,
} from "@overmap-ai/common"
import { selectLatestProcedureTypeFieldsOfProcedureType, selectProcedureStepsOfProcedureType } from "@overmap-ai/core"
import { extractFilesAndPromisesFromFieldValues, type Field, type FieldValues } from "@overmap-ai/forms"
import * as RadixAccordian from "@radix-ui/react-accordion"
import { cx } from "class-variance-authority"
import { Fragment, memo, useCallback } from "react"
import { v4 as uuid } from "uuid"

import { interactiveCardCva } from "../../cva"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import {
	selectAttachmentsByProcedureFieldValues,
	selectLatestFieldValuesByProcedureOfProcedureInitiative,
	selectProceduresByAssetOfProcedureInitiative,
} from "../../store/selectors"
import { useDeserializeFieldSchema } from "../Form/hooks"
import { ProcedureTypeFieldsSubmitter } from "../Form/ProcedureTypeFieldsSubmitter"
import { getAttachmentPayloadsFromFilesAndPromises, getFieldValuesAttachmentsByFieldId } from "../Form/utils"
import { ProcedureTypeIcon } from "../Icons/ProcedureTypeIcon"
import { ProcedureInitiativeIndexLabel } from "../IndexLabels/ProcedureInitiativeIndexLabel"
import { loadingToast } from "../Toasts"
import { BulkAssetControlProcedureStepItem } from "./BulkAssetControlProcedureStepItem"

interface BulkAssetControlProcedureInitiativeItemProps {
	procedureInitiative: Stored<ProcedureInitiative>
	assetIds: Asset["uuid"][]
}

export const BulkAssetControlProcedureInitiativeItem = memo<BulkAssetControlProcedureInitiativeItemProps>((props) => {
	const { procedureInitiative, assetIds } = props

	const procedureSteps = useShallowEqaulSelector(
		selectProcedureStepsOfProcedureType(procedureInitiative.procedure_type),
	)
	const latestProcedureTypeFields = useAppSelector(
		selectLatestProcedureTypeFieldsOfProcedureType(procedureInitiative.procedure_type),
	)
	const proceduresByAsset = useShallowEqaulSelector(
		selectProceduresByAssetOfProcedureInitiative(procedureInitiative.uuid),
	)
	const latestProcedureFieldValuesByProcedure = useShallowEqaulSelector(
		selectLatestFieldValuesByProcedureOfProcedureInitiative(procedureInitiative.uuid),
	)
	const attachmentsByProcedureFieldValues = useAppSelector(selectAttachmentsByProcedureFieldValues)
	const openAlertDialog = useAlertDialog()
	const fieldSchema = useDeserializeFieldSchema(latestProcedureTypeFields?.fields ?? EMPTY_ARRAY)

	const submitProcedureTypeFields = useCallback(
		async (allValues: FieldValues) => {
			if (!latestProcedureTypeFields?.uuid) return
			if (fieldSchema.areValuesBlank(allValues)) return

			const changedValues = fieldSchema.changedFieldValues(fieldSchema.blankValues(), allValues)
			const loadingToastId = uuid()

			loadingToast({
				id: loadingToastId,
				description: `Submitting values for ${assetIds.length} assets.`,
			})

			const [values, filesAndPromises] = extractFilesAndPromisesFromFieldValues(
				fieldSchema.getFields(),
				changedValues,
			)

			const fieldValuesByProcedure: Record<Procedure["uuid"], FieldValues> = {}
			const fieldValuesAttachmentsByFieldIdByProcedure: Record<
				Procedure["uuid"],
				Record<Field["id"], Stored<ProcedureFieldValuesAttachment>[]>
			> = {}

			for (const assetId of assetIds) {
				if (!(assetId in proceduresByAsset)) continue

				const procedure = proceduresByAsset[assetId]!

				const procedureFieldValues = latestProcedureFieldValuesByProcedure[procedure.uuid]
				const attachments = attachmentsByProcedureFieldValues[procedureFieldValues?.uuid ?? ""] ?? []

				const fieldValues = fieldSchema.deserializeValues(procedureFieldValues?.values ?? {})
				const attachmentsByFieldId = getFieldValuesAttachmentsByFieldId(attachments)

				// Hydrate values with attachments?
				// separate each?

				for (const fieldId in values) {
					if (fieldId in attachmentsByFieldId) delete attachmentsByFieldId[fieldId]
				}

				fieldValuesByProcedure[procedure.uuid] = { ...fieldValues, ...values }
				fieldValuesAttachmentsByFieldIdByProcedure[procedure.uuid] = attachmentsByFieldId
			}

			const [offlineProcedureFieldValues, promise] = sdk.procedureFieldValues.bulkAdd(
				Object.entries(fieldValuesByProcedure).map(([procedureId, values]) => ({
					procedure: procedureId,
					values: fieldSchema.serializeValues(values),
					fields_revision: latestProcedureTypeFields.uuid,
					published_at: new Date().toISOString(),
				})),
			)

			void sdk.procedureFieldValuesAttachments.bulkAdd(
				offlineProcedureFieldValues.flatMap(({ uuid, procedure }) => {
					const attachmentsByFieldId = fieldValuesAttachmentsByFieldIdByProcedure[procedure] ?? []
					return getAttachmentPayloadsFromFilesAndPromises(uuid, filesAndPromises, []).concat(
						Object.entries(attachmentsByFieldId).flatMap(([fieldId, attachments]) =>
							attachments.map((attachment) => ({
								field_values: uuid,
								field_identifier: fieldId,
								file_sha1: attachment.file_sha1,
								file_extension: attachment.file_extension,
								file_name: attachment.file_name,
								file_type: attachment.file_type,
								object_url: attachment.object_url,
							})),
						),
					)
				}),
			)

			void promise
				.then(() => {
					toast.success({
						title: "Success",
						description: `Fields set on ${assetIds.length} assets.`,
					})
				})
				.catch((e: unknown) => {
					toast.error({
						title: "Failed to set fields",
						description: e instanceof Error ? e.message : ERROR_MSG_UNEXPECTED,
					})
				})
				.finally(() => {
					toast.dismiss(loadingToastId)
				})
		},
		[
			proceduresByAsset,
			latestProcedureTypeFields?.uuid,
			assetIds,
			fieldSchema,
			latestProcedureFieldValuesByProcedure,
			attachmentsByProcedureFieldValues,
		],
	)

	const handleFieldsSubmit = useCallback(
		(values: FieldValues) => {
			openAlertDialog({
				title: "Confirm mass fields submission?",
				description: `You are about to set fields on ${assetIds.length} assets. Are you sure?`,
				size: "sm",
				onAction: () => submitProcedureTypeFields(values),
			})
		},
		[openAlertDialog, assetIds.length, submitProcedureTypeFields],
	)

	return (
		<RadixAccordian.Item value={procedureInitiative.uuid} asChild>
			<Card className={cx("flex flex-col gap-4", interactiveCardCva())} role="listitem" size="sm">
				<RadixAccordian.Trigger asChild>
					<ButtonGroup
						className="group flex w-full cursor-pointer items-center justify-between gap-2"
						tabIndex={0}
						size="sm"
						variant="ghost"
						accentColor="base"
					>
						<div className="flex min-w-0 items-center gap-2">
							<ProcedureTypeIcon procedureTypeId={procedureInitiative.procedure_type} />
							<ProcedureInitiativeIndexLabel
								size="sm"
								procedureInitiativeId={procedureInitiative.uuid}
								className="truncate"
							/>
						</div>
					</ButtonGroup>
				</RadixAccordian.Trigger>

				<RadixAccordian.Content className="flex flex-col gap-4">
					{!!latestProcedureTypeFields && (
						<>
							<Separator size="full" />
							<ProcedureTypeFieldsSubmitter
								size="xs"
								procedureTypeFields={latestProcedureTypeFields}
								onSubmit={handleFieldsSubmit}
								hideCancel
							/>
						</>
					)}

					{procedureSteps.length > 0 && (
						<>
							<Separator size="full" />
							<RadixAccordian.Root className="flex flex-col gap-4" type="single" collapsible role="list">
								{procedureSteps.map((procedureStep, index) => (
									<Fragment key={procedureStep.uuid}>
										<BulkAssetControlProcedureStepItem
											assetIds={assetIds}
											procedureInitiative={procedureInitiative}
											procedureStep={procedureStep}
										/>
										{index !== procedureSteps.length - 1 && <Separator size="full" />}
									</Fragment>
								))}
							</RadixAccordian.Root>
						</>
					)}
				</RadixAccordian.Content>
			</Card>
		</RadixAccordian.Item>
	)
})

BulkAssetControlProcedureInitiativeItem.displayName = "BulkAssetControlProcedureInitiativeItem"
